import { getSlotDates as slotDatesApiGet } from 'api/definitions/slotdates';

import { SlotTypes } from 'constants/fulfilmentTypes';

import { SLOT_DATES_REQUEST_ACTIONS } from 'redux/modules/slot-dates/actions/types';
import { type SlotDatesState } from 'redux/modules/slot-dates/state/initial-state';
import { SET_SLOTS_DISABLED } from 'redux/modules/slots/actions/types';
import { customerSlotSelector, hasBookedSlot } from 'redux/modules/customer-slot/selectors';
import { setSelectedSlotGridType } from 'redux/modules/slot-booking/actions/set-selected-slot-grid-type';
import { getFiledSlotTimes } from 'api/definitions/filed-slot-times';

export const getSlotDates =
  (
    branchId: string,
    addressId: string,
    fromDate: string,
    slotType: SlotTypes = SlotTypes.DELIVERY,
    customerOrderId = -1,
    size = 93,
  ) =>
  async (dispatch: WtrDispatch, getState: () => WtrState) => {
    const missingDeliveryAddressId = slotType === SlotTypes.DELIVERY && !addressId.trim();
    const missingCollectionBranchId = slotType !== SlotTypes.DELIVERY && !branchId?.trim();

    if (missingDeliveryAddressId || missingCollectionBranchId) {
      return undefined;
    }

    try {
      dispatch({
        type: SLOT_DATES_REQUEST_ACTIONS.request,
      });

      const [{ content: slotDatesRaw }, slotTimes]: [
        { content: { id: string; dayOfWeek: string }[] },
        SlotDatesState['times'],
      ] = await Promise.all([
        slotDatesApiGet({
          ...(slotType === SlotTypes.DELIVERY ? { addressId } : { branchId }),
          slotType,
          customerOrderId,
          fromDate,
          size,
          iHandleStatusCodes: [307],
        })({
          dispatch,
          getState,
        }),
        getFiledSlotTimes({
          ...(slotType === SlotTypes.DELIVERY ? { addressId } : { branchId }),
          slotType,
          fromDate,
          size,
          iHandleStatusCodes: [307],
        })({ dispatch, getState }),
      ]);

      const state = getState();
      const customerSlot = customerSlotSelector(state);

      if (
        hasBookedSlot(state) &&
        slotTimes.find(time => time.slotGridType === customerSlot.slotGridType)
      ) {
        dispatch(setSelectedSlotGridType(customerSlot.slotGridType));
      }

      // This data was previously transformed in the GraphQL layer. Starting from scratch, I would keep it as it is,
      // but mapping at point of entry in to Redux means many places do not need to be updated and checked.
      const slotDatesMapped = slotDatesRaw.map(({ id, dayOfWeek }) => ({ date: id, dayOfWeek }));

      return dispatch({
        type: SLOT_DATES_REQUEST_ACTIONS.success,
        payload: {
          slotDates: slotDatesMapped,
          slotTimes,
        },
      });
    } catch (e: unknown) {
      if (typeof e === 'object' && e !== null && 'status' in e && e.status === 307) {
        return dispatch({
          type: SET_SLOTS_DISABLED,
        });
      }
      return dispatch({
        type: SLOT_DATES_REQUEST_ACTIONS.failure,
        payload:
          typeof e === 'object' && e !== null && 'message' in e ? e.message : 'Unknown error',
      });
    }
  };
